import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getAgentCommissionsSummaryAsync } from '@share/api';
import { ILoginState, IMenuState } from '@share/store/slices';

interface IAccessState {
  loginStore: ILoginState;
  navigationMenuStore: IMenuState;
}

// One lookup per member per page load: the header renders the link twice (desktop and drawer).
const lookups = new Map<string, Promise<boolean>>();

const hasCommissionsFor = (memberKey: string) => {
  if (!lookups.has(memberKey)) {
    lookups.set(
      memberKey,
      getAgentCommissionsSummaryAsync()
        .then(({ data }) => (data?.ready ?? 0) + (data?.pending ?? 0) + (data?.paidOutThisYear ?? 0) > 0)
        .catch(() => false),
    );
  }

  return lookups.get(memberKey) as Promise<boolean>;
};

/**
 * Whether the member gets the Commissions page: current Levarti travel agents, and members of a
 * Levarti org who have commissions from bookings they made while they were one.
 */
export function useCommissionsAccess(): boolean {
  const { account } = useSelector((state: IAccessState) => state.loginStore);
  const { items } = useSelector((state: IAccessState) => state.navigationMenuStore);

  const isLevartiMember = !!account?.levartiOrgIds?.includes(items?.organizationId as number);
  const isAgent = isLevartiMember && !!items?.isTravelAgent;
  const memberKey = String(items?.rsiId ?? '');

  const [hasCommissions, setHasCommissions] = useState(false);

  useEffect(() => {
    if (isAgent || !isLevartiMember) {
      return;
    }

    let isCancelled = false;
    hasCommissionsFor(memberKey).then((value) => {
      if (!isCancelled) {
        setHasCommissions(value);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [isAgent, isLevartiMember, memberKey]);

  return isAgent || hasCommissions;
}
